import i18next from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';

/** Sales 브랜드 권한 변경 신청 — 레거시 appr_edit_forms_sales_brand_permission_request */
export const SALES_BRAND_PERMISSION_TYPE = 215;

export const isSalesBrandPermissionType = (apvType: number): boolean =>
  apvType === SALES_BRAND_PERMISSION_TYPE;

export const SALES_BRAND_NO_CHANGE_MESSAGE =
  '변경사항이 없습니다. 체크 항목을 다시 확인해 주세요.';
export const SALES_BRAND_LOAD_FAILED_MESSAGE =
  '브랜드 리스트를 불러올 수 없습니다.';

export const SALES_BRAND_PERMISSION_SUBJECT = 'Sales 브랜드 권한 변경 신청';

/** 레거시 brandTranslations — 없는 코드는 API 이름 그대로 */
const BRAND_NAMES: Readonly<Record<string, string>> = {
  X: 'Discovery',
  XC: 'Discovery China',
  XT: 'Discovery Taiwan',
  XH: 'Discovery Hong Kong',
  M: 'MLB',
  MC: 'MLB China',
  MH: 'MLB Hong Kong',
  MT: 'MLB Taiwan',
  I: 'MLB Kids',
  IC: 'MLB Kids China',
  V: 'Duvetica',
  VC: 'Duvetica China',
  W: 'Supra',
  WC: 'Supra China',
  ST: 'Sergio Tacchini',
};

/** GET /type/215/draft-source 응답 data */
export type SalesBrandDraftSource = {
  code: number;
  attributes: Record<string, never>;
  display: {
    brands: { brandCode: string; brandName: string; granted: boolean }[];
  };
};

export type SalesBrand = { code: string; name: string; granted: boolean };

export type SalesBrandChanges = {
  addedBrands: string[];
  removedBrands: string[];
};

export type SalesBrandPermissionState = {
  /** draft-source 도착 전이면 null */
  brands: SalesBrand[] | null;
  /** 지금 체크된 브랜드 코드 */
  checked: string[];
  /** 편집 진입 시 저장돼 있던 변경분 — 브랜드 목록이 오면 현재 권한 위에 다시 얹음 */
  saved: SalesBrandChanges | null;
};

export const createSalesBrandPermissionState = (
  saved: SalesBrandChanges | null = null,
): SalesBrandPermissionState => ({ brands: null, checked: [], saved });

/** 레거시 renderBrandList — 표시 이름 가나다순 */
export const toSalesBrands = (
  source: SalesBrandDraftSource | null | undefined,
): SalesBrand[] =>
  (source?.display.brands ?? [])
    .map((b) => ({
      code: b.brandCode,
      name: BRAND_NAMES[b.brandCode] ?? b.brandName,
      granted: b.granted,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'ko-KR'));

/**
 * 현재 보유 권한을 기준으로 저장 변경분을 다시 체크. 그 사이 이미 생긴 권한의 추가,
 * 이미 빠진 권한의 회수는 변경 없음이 됨
 */
export const applySavedChanges = (
  brands: SalesBrand[],
  saved: SalesBrandChanges | null,
): string[] =>
  brands
    .filter((b) =>
      b.granted
        ? !saved?.removedBrands.includes(b.code)
        : !!saved?.addedBrands.includes(b.code),
    )
    .map((b) => b.code);

export const getSalesBrandChanges = (
  state: SalesBrandPermissionState | null | undefined,
): SalesBrandChanges => {
  if (!state?.brands) return { addedBrands: [], removedBrands: [] };
  const checked = new Set(state.checked);
  return {
    addedBrands: state.brands
      .filter((b) => !b.granted && checked.has(b.code))
      .map((b) => b.code),
    removedBrands: state.brands
      .filter((b) => b.granted && !checked.has(b.code))
      .map((b) => b.code),
  };
};

/** 번역 리소스 로드 전이면 원문 */
const tr = (key: string, n?: number): string => {
  const translated = i18next.t(key, { n });
  return (
    typeof translated === 'string' && translated ? translated : key
  ).replace('{{n}}', String(n));
};

/** 레거시 updateSubject — 작성자 언어로 저장 */
export const buildSalesBrandPermissionSubject = (
  name: string,
  changes?: SalesBrandChanges,
): string => {
  const parts: string[] = [];
  if (changes?.addedBrands.length) {
    parts.push(tr('추가 {{n}}건', changes.addedBrands.length));
  }
  if (changes?.removedBrands.length) {
    parts.push(tr('회수 {{n}}건', changes.removedBrands.length));
  }
  const suffix = parts.length ? ` / ${tr('브랜드')} ${parts.join(' / ')}` : '';
  return `${tr(SALES_BRAND_PERMISSION_SUBJECT)} (${name}${suffix})`;
};

const toCodes = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string')
    : [];

/** 저장 attributes 의 변경분. 둘 다 비면 null */
export const parseSalesBrandChanges = (
  attributes: unknown,
): SalesBrandChanges | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const raw = attributes as Record<string, unknown>;
  const changes = {
    addedBrands: toCodes(raw.addedBrands),
    removedBrands: toCodes(raw.removedBrands),
  };
  return changes.addedBrands.length || changes.removedBrands.length
    ? changes
    : null;
};

export const parseSalesBrandPermissionContent = (
  attributes: unknown,
): string | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const raw = (attributes as Record<string, unknown>).content;
  return typeof raw === 'string' && raw.trim() ? raw : null;
};

/** 레거시부터 임시저장도 같은 검사 */
export const checkSalesBrandPermissionBeforeSave = (
  state: SalesBrandPermissionState | null | undefined,
): string | null => {
  const { addedBrands, removedBrands } = getSalesBrandChanges(state);
  return addedBrands.length || removedBrands.length
    ? null
    : SALES_BRAND_NO_CHANGE_MESSAGE;
};

export const buildSalesBrandPermissionAttributes = (
  state: SalesBrandPermissionState | null | undefined,
  content: string,
): ApprovalAttributes | null => {
  if (!state?.brands) return null;
  return { content, ...getSalesBrandChanges(state) };
};

/** 이탈 판정 — 목록 도착 전엔 저장 변경분, 도착 후엔 현재 변경분 */
export const salesBrandPermissionKey = (
  state: SalesBrandPermissionState | null,
): string => {
  if (!state) return '';
  const changes = state.brands ? getSalesBrandChanges(state) : state.saved;
  if (!changes?.addedBrands.length && !changes?.removedBrands.length) {
    return '';
  }
  return `${changes.addedBrands.join(',')}|${changes.removedBrands.join(',')}`;
};
